import React, { useState, useEffect } from "react";
import {
  ShoppingBag,
  Upload,
  RefreshCw,
  Link,
  CheckCircle,
  AlertCircle,
  Search,
  Settings,
  Volume2,
  PhoneCall,
  DollarSign,
  Package,
  Plus,
  Pencil,
  Trash2,
  Sparkles,
  Image as ImageIcon,
  Video,
  X,
  HelpCircle,
  ArrowUp,
  ArrowDown,
  MessageSquare,
  Bot,
  ListOrdered,
} from "lucide-react";

interface CatalogViewProps {
  token: string;
  user: any;
  tenantId?: string;
}

interface IntakeQuestion {
  id: string;
  tenant_id: string;
  sequence_order: number;
  question_text: string;
  field_key: string;
  quick_replies: string[];
  is_required: boolean;
  is_active: boolean;
  help_text?: string | null;
  created_at: string;
  updated_at: string;
}

interface CatalogItem {
  id: string;
  sku: string;
  name: string;
  category: string | null;
  price: number;
  discounted_price: number;
  stock_quantity: number;
  stock_status: string;
  image_url: string | null;
  image_urls?: string[];
  video_url?: string | null;
  video_urls?: string[];
  description: string | null;
  faqs?: Array<{ question: string; answer: string }>;
  is_active: boolean;
}

interface AISalesProfile {
  business_name: string;
  business_description: string | null;
  business_category: string | null;
  voice_persona: "aarohi" | "kabir";
  is_enabled: boolean;
  tax_inclusive: boolean;
  tax_percentage: number;
  free_shipping_threshold: number;
  standard_shipping_fee: number;
  sales_rep_phone: string | null;
  quiet_hours_start: number;
  quiet_hours_end: number;
  welcome_message: string | null;
  intake_questions_enabled?: boolean;
  intake_intro_message?: string | null;
  intake_completion_message?: string | null;
  ai_provider?: string;
  ai_api_key?: string | null;
  ai_model_name?: string | null;
  sales_tone?: string;
  language_mode?: string;
  last_trained_at?: string | null;
  training_summary?: any;
}

interface AITrainingStatus {
  last_trained_at: string | null;
  training_summary: any;
  items_count: number;
  images_count: number;
  videos_count: number;
  needs_retrain: boolean;
}

export const CatalogView: React.FC<CatalogViewProps> = ({ token, user, tenantId }) => {
  const effectiveTenantId =
    user.role === "tenant"
      ? user.tenant_id
      : tenantId && tenantId !== "all"
      ? tenantId
      : user.tenant_id;

  const [activeTab, setActiveTab] = useState<"catalog" | "settings" | "questions">("catalog");
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Question Module states
  const [questions, setQuestions] = useState<IntakeQuestion[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<IntakeQuestion | null>(null);
  const [questionFormData, setQuestionFormData] = useState({
    sequence_order: 1,
    question_text: "",
    field_key: "",
    quick_replies: [] as string[],
    quick_reply_input: "",
    is_required: true,
    is_active: true,
    help_text: "",
  });
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [questionBannerMsg, setQuestionBannerMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // AI Training state
  const [trainingStatus, setTrainingStatus] = useState<AITrainingStatus | null>(null);
  const [isTrainingAI, setIsTrainingAI] = useState(false);
  const [trainBannerMsg, setTrainBannerMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Profile settings
  const [profile, setProfile] = useState<AISalesProfile>({
    business_name: "My Business",
    business_description: "",
    business_category: "Retail",
    voice_persona: "aarohi",
    is_enabled: false,
    tax_inclusive: true,
    tax_percentage: 18.0,
    free_shipping_threshold: 999.0,
    standard_shipping_fee: 60.0,
    sales_rep_phone: "",
    quiet_hours_start: 21,
    quiet_hours_end: 9,
    welcome_message: "",
    intake_questions_enabled: true,
    intake_intro_message: "",
    intake_completion_message: "",
    ai_provider: "auto",
    ai_api_key: "",
    ai_model_name: "",
    sales_tone: "consultative_boutique",
    language_mode: "adaptive_hinglish",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSavedMsg, setProfileSavedMsg] = useState("");

  // Sheet import
  const [sheetUrl, setSheetUrl] = useState("");
  const [importingSheet, setImportingSheet] = useState(false);
  const [importMsg, setImportMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Add / Edit Product Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CatalogItem | null>(null);
  const [formData, setFormData] = useState({
    sku: "",
    name: "",
    category: "",
    price: 0,
    discounted_price: 0,
    stock_quantity: 10,
    stock_status: "in_stock",
    description: "",
    image_urls: [] as string[],
    video_url: "",
    video_urls: [] as string[],
    faqs: [] as Array<{ question: string; answer: string }>,
  });
  const [imageUrlInput, setImageUrlInput] = useState("");
  const [videoUrlInput, setVideoUrlInput] = useState("");
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [savingItem, setSavingItem] = useState(false);

  // Media Gallery Preview Modal (for table row inspection)
  const [previewMediaItem, setPreviewMediaItem] = useState<CatalogItem | null>(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const url = new URL("/api/v1/catalog/items", window.location.origin);
      if (searchQuery) url.searchParams.set("search", searchQuery);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setTotalCount(data.total || 0);
      }
    } catch (err) {
      console.error("Failed to fetch catalog items", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const url = new URL("/api/v1/catalog/ai-profile", window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
      }
    } catch (err) {
      console.error("Failed to fetch AI sales profile", err);
    }
  };

  const fetchTrainingStatus = async () => {
    try {
      const url = new URL("/api/v1/catalog/ai-training-status", window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTrainingStatus(data);
      }
    } catch (err) {
      console.error("Failed to fetch AI training status", err);
    }
  };

  const fetchQuestions = async () => {
    setLoadingQuestions(true);
    try {
      const url = new URL("/api/v1/catalog/questions", window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setQuestions(data || []);
      }
    } catch (err) {
      console.error("Failed to fetch questions", err);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const openAddQuestionModal = () => {
    const nextSeq = questions.length > 0 ? Math.max(...questions.map((q) => q.sequence_order)) + 1 : 1;
    setEditingQuestion(null);
    setQuestionFormData({
      sequence_order: nextSeq,
      question_text: "",
      field_key: "",
      quick_replies: [],
      quick_reply_input: "",
      is_required: true,
      is_active: true,
      help_text: "",
    });
    setIsQuestionModalOpen(true);
  };

  const openEditQuestionModal = (q: IntakeQuestion) => {
    setEditingQuestion(q);
    setQuestionFormData({
      sequence_order: q.sequence_order,
      question_text: q.question_text,
      field_key: q.field_key,
      quick_replies: q.quick_replies || [],
      quick_reply_input: "",
      is_required: q.is_required,
      is_active: q.is_active,
      help_text: q.help_text || "",
    });
    setIsQuestionModalOpen(true);
  };

  const handleSaveQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!questionFormData.question_text.trim()) {
      alert("Please enter question text");
      return;
    }
    const cleanKey = (questionFormData.field_key.trim() || questionFormData.question_text.trim().slice(0, 30))
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, "_")
      .replace(/_+/g, "_");

    setSavingQuestion(true);
    try {
      const payload = {
        sequence_order: Number(questionFormData.sequence_order) || 1,
        question_text: questionFormData.question_text.trim(),
        field_key: cleanKey,
        quick_replies: questionFormData.quick_replies,
        is_required: questionFormData.is_required,
        is_active: questionFormData.is_active,
        help_text: questionFormData.help_text.trim() || null,
      };

      let res: Response;
      if (editingQuestion) {
        const url = new URL(`/api/v1/catalog/questions/${editingQuestion.id}`, window.location.origin);
        if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
        res = await fetch(url.toString(), {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      } else {
        const url = new URL("/api/v1/catalog/questions", window.location.origin);
        if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
        res = await fetch(url.toString(), {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      }

      if (res.ok) {
        setIsQuestionModalOpen(false);
        setEditingQuestion(null);
        fetchQuestions();
        setQuestionBannerMsg({ type: "success", text: "Question saved successfully!" });
      } else {
        const data = await res.json();
        alert(data.detail || "Failed to save question");
      }
    } catch (err: any) {
      alert(err.message || "Failed to save question");
    } finally {
      setSavingQuestion(false);
    }
  };

  const handleDeleteQuestion = async (id: string, text: string) => {
    if (!confirm(`Are you sure you want to delete this question?\n\n"${text}"`)) return;
    try {
      const url = new URL(`/api/v1/catalog/questions/${id}`, window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchQuestions();
        setQuestionBannerMsg({ type: "success", text: "Question deleted!" });
      }
    } catch (err) {
      console.error("Failed to delete question", err);
    }
  };

  const handleMoveQuestion = async (index: number, direction: "up" | "down") => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= questions.length) return;
    const newOrder = [...questions];
    const currItem = newOrder[index];
    const targetItem = newOrder[targetIndex];
    if (!currItem || !targetItem) return;
    newOrder[index] = targetItem;
    newOrder[targetIndex] = currItem;
    setQuestions(newOrder);

    try {
      const url = new URL("/api/v1/catalog/questions/reorder", window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ question_ids: newOrder.map((q) => q.id) }),
      });
      if (res.ok) {
        const data = await res.json();
        setQuestions(data);
      }
    } catch (err) {
      console.error("Failed to reorder questions", err);
      fetchQuestions();
    }
  };

  const handleToggleQuestionActive = async (q: IntakeQuestion) => {
    try {
      const url = new URL(`/api/v1/catalog/questions/${q.id}`, window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ is_active: !q.is_active }),
      });
      if (res.ok) {
        fetchQuestions();
      }
    } catch (err) {
      console.error("Failed to toggle question active state", err);
    }
  };

  const handleSeedDefaults = async () => {
    const confirmMsg =
      questions.length > 0
        ? "Do you want to reset and re-seed standard qualification questions for your business?"
        : "Seed standard qualification questions for your business?";
    if (!confirm(confirmMsg)) return;

    try {
      const url = new URL("/api/v1/catalog/questions/seed-defaults", window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      url.searchParams.set("reset", questions.length > 0 ? "true" : "false");
      const res = await fetch(url.toString(), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setQuestions(data);
        fetchProfile();
        setQuestionBannerMsg({
          type: "success",
          text: `Standard qualification questions seeded (${data.length} questions)!`,
        });
      }
    } catch (err) {
      console.error("Failed to seed default questions", err);
    }
  };

  const addQuickReply = () => {
    const val = questionFormData.quick_reply_input.trim();
    if (!val) return;
    if (questionFormData.quick_replies.includes(val)) return;
    setQuestionFormData((prev) => ({
      ...prev,
      quick_replies: [...prev.quick_replies, val],
      quick_reply_input: "",
    }));
  };

  const removeQuickReply = (idx: number) => {
    setQuestionFormData((prev) => ({
      ...prev,
      quick_replies: prev.quick_replies.filter((_, i) => i !== idx),
    }));
  };

  useEffect(() => {
    fetchItems();
    fetchProfile();
    fetchTrainingStatus();
    fetchQuestions();
  }, [effectiveTenantId]);

  const handleTrainAI = async () => {
    setIsTrainingAI(true);
    setTrainBannerMsg(null);
    try {
      const url = new URL("/api/v1/catalog/train-ai", window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setTrainBannerMsg({
          type: "success",
          text:
            data.message ||
            `AI Session trained successfully! ${data.items_count} items, ${data.images_count} images, and ${data.videos_count} videos are now active in the bot knowledge base.`,
        });
        fetchTrainingStatus();
        fetchProfile();
      } else {
        setTrainBannerMsg({
          type: "error",
          text: data.detail || "AI Session training failed",
        });
      }
    } catch (err: any) {
      setTrainBannerMsg({
        type: "error",
        text: err.message || "Failed to trigger AI session training",
      });
    } finally {
      setIsTrainingAI(false);
    }
  };

  const openAddModal = () => {
    setEditingItem(null);
    setFormData({
      sku: "",
      name: "",
      category: "",
      price: 0,
      discounted_price: 0,
      stock_quantity: 10,
      stock_status: "in_stock",
      description: "",
      image_urls: [],
      video_url: "",
      video_urls: [],
      faqs: [],
    });
    setImageUrlInput("");
    setVideoUrlInput("");
    setIsModalOpen(true);
  };

  const openEditModal = (item: CatalogItem) => {
    setEditingItem(item);
    const existingImages =
      item.image_urls && item.image_urls.length > 0
        ? item.image_urls
        : item.image_url
        ? [item.image_url]
        : [];
    const existingVideos =
      item.video_urls && item.video_urls.length > 0
        ? item.video_urls
        : item.video_url
        ? [item.video_url]
        : [];

    setFormData({
      sku: item.sku || "",
      name: item.name || "",
      category: item.category || "",
      price: Number(item.price) || 0,
      discounted_price: Number(item.discounted_price) || Number(item.price) || 0,
      stock_quantity: item.stock_quantity ?? 10,
      stock_status: item.stock_status || "in_stock",
      description: item.description || "",
      image_urls: existingImages,
      video_url: item.video_url || existingVideos[0] || "",
      video_urls: existingVideos,
      faqs: item.faqs || [],
    });
    setImageUrlInput("");
    setVideoUrlInput("");
    setIsModalOpen(true);
  };

  const handleUploadImages = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingMedia(true);
    try {
      const uploaded: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file) continue;
        const form = new FormData();
        form.append("file", file);
        const url = new URL("/api/v1/catalog/upload-media", window.location.origin);
        if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
        const res = await fetch(url.toString(), {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
        if (res.ok) {
          const data = await res.json();
          if (data.url) uploaded.push(data.url);
        }
      }
      setFormData((prev) => ({
        ...prev,
        image_urls: [...prev.image_urls, ...uploaded],
      }));
    } catch (err) {
      console.error("Failed to upload images", err);
    } finally {
      setUploadingMedia(false);
      e.target.value = "";
    }
  };

  const handleUploadVideo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMedia(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const url = new URL("/api/v1/catalog/upload-media", window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) {
          setFormData((prev) => ({
            ...prev,
            video_url: data.url,
            video_urls: [data.url],
          }));
        }
      }
    } catch (err) {
      console.error("Failed to upload video", err);
    } finally {
      setUploadingMedia(false);
      e.target.value = "";
    }
  };

  const addImageUrl = () => {
    if (!imageUrlInput.trim()) return;
    setFormData((prev) => ({
      ...prev,
      image_urls: [...prev.image_urls, imageUrlInput.trim()],
    }));
    setImageUrlInput("");
  };

  const removeImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      image_urls: prev.image_urls.filter((_, idx) => idx !== index),
    }));
  };

  const addVideoUrl = () => {
    if (!videoUrlInput.trim()) return;
    setFormData((prev) => ({
      ...prev,
      video_url: videoUrlInput.trim(),
      video_urls: [videoUrlInput.trim()],
    }));
    setVideoUrlInput("");
  };

  const removeVideo = () => {
    setFormData((prev) => ({
      ...prev,
      video_url: "",
      video_urls: [],
    }));
  };

  const handleSaveItem = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingItem(true);
    try {
      const payload = {
        ...formData,
        sku: formData.sku.trim() || undefined,
        name: formData.name.trim(),
        category: formData.category.trim() || null,
        price: Number(formData.price),
        discounted_price: Number(formData.discounted_price) || Number(formData.price),
        stock_quantity: Number(formData.stock_quantity),
        description: formData.description.trim() || null,
        image_url: formData.image_urls.length > 0 ? formData.image_urls[0] : null,
        image_urls: formData.image_urls,
        video_url: formData.video_url.trim() || null,
        video_urls:
          formData.video_urls.length > 0
            ? formData.video_urls
            : formData.video_url
            ? [formData.video_url.trim()]
            : [],
      };

      let res: Response;
      if (editingItem) {
        const url = new URL(`/api/v1/catalog/items/${editingItem.id}`, window.location.origin);
        if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
        res = await fetch(url.toString(), {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      } else {
        const url = new URL("/api/v1/catalog/items", window.location.origin);
        if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
        res = await fetch(url.toString(), {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      }

      if (res.ok) {
        setIsModalOpen(false);
        setEditingItem(null);
        fetchItems();
        fetchTrainingStatus();
        setTrainBannerMsg({
          type: "success",
          text: "Item saved! Remember to click 'Train AI Session' to update your WhatsApp bot.",
        });
      } else {
        const data = await res.json();
        alert(data.detail || "Failed to save item");
      }
    } catch (err: any) {
      alert(err.message || "Failed to save item");
    } finally {
      setSavingItem(false);
    }
  };

  const handleDeleteItem = async (itemId: string, itemName: string) => {
    if (!confirm(`Are you sure you want to delete "${itemName}"?`)) return;
    try {
      const url = new URL(`/api/v1/catalog/items/${itemId}`, window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchItems();
        fetchTrainingStatus();
      }
    } catch (err) {
      console.error("Failed to delete item", err);
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileSavedMsg("");
    try {
      const url = new URL("/api/v1/catalog/ai-profile", window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(profile),
      });
      if (res.ok) {
        setProfileSavedMsg("AI settings saved successfully!");
        setTimeout(() => setProfileSavedMsg(""), 4000);
      }
    } catch (err) {
      console.error("Failed to save profile", err);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleImportSheet = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sheetUrl) return;
    setImportingSheet(true);
    setImportMsg(null);
    try {
      const url = new URL("/api/v1/catalog/import-sheet", window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ sheet_url: sheetUrl }),
      });
      const data = await res.json();
      if (res.ok) {
        setImportMsg({
          type: "success",
          text: `Success! Imported ${data.imported_count} new items, updated ${data.updated_count} existing items.`,
        });
        setSheetUrl("");
        fetchItems();
        fetchTrainingStatus();
      } else {
        setImportMsg({ type: "error", text: data.detail || "Import failed" });
      }
    } catch (err: any) {
      setImportMsg({ type: "error", text: err.message || "Failed to sync Google Sheet" });
    } finally {
      setImportingSheet(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    setImportingSheet(true);
    setImportMsg(null);
    try {
      const url = new URL("/api/v1/catalog/import-file", window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formDataUpload,
      });
      const data = await res.json();
      if (res.ok) {
        setImportMsg({
          type: "success",
          text: `File uploaded! Imported ${data.imported_count} items, updated ${data.updated_count} items.`,
        });
        fetchItems();
        fetchTrainingStatus();
      } else {
        setImportMsg({ type: "error", text: data.detail || "Import failed" });
      }
    } catch (err: any) {
      setImportMsg({ type: "error", text: err.message || "File upload failed" });
    } finally {
      setImportingSheet(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-900 text-white p-6 rounded-2xl shadow-xl">
        <div>
          <div className="flex items-center gap-3">
            <ShoppingBag className="w-8 h-8 text-emerald-400" />
            <h1 className="text-2xl font-bold tracking-tight">Catalog & Autonomous AI Sales</h1>
          </div>
          <p className="text-slate-300 text-sm mt-1">
            Manage products/services with multiple images & video, and train your WhatsApp AI sales bot session.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-sm font-semibold shadow-lg shadow-emerald-900/40 transition"
          >
            <Plus className="w-4 h-4" /> Add Product / Service
          </button>
          <div className="flex bg-slate-800 p-1 rounded-xl border border-slate-700">
            <button
              onClick={() => setActiveTab("catalog")}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-medium transition ${
                activeTab === "catalog"
                  ? "bg-slate-700 text-white shadow-sm"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              <Package className="w-4 h-4" /> Products ({totalCount})
            </button>
            <button
              onClick={() => setActiveTab("settings")}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-medium transition ${
                activeTab === "settings"
                  ? "bg-slate-700 text-white shadow-sm"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              <Settings className="w-4 h-4" /> AI Voice & Persona
            </button>
            <button
              onClick={() => setActiveTab("questions")}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-medium transition ${
                activeTab === "questions"
                  ? "bg-slate-700 text-white shadow-sm"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              <HelpCircle className="w-4 h-4" /> Question Module ({questions.length})
            </button>
          </div>
        </div>
      </div>

      {/* AI Bot Knowledge & Retrain Bar */}
      <div className="bg-gradient-to-r from-emerald-950/70 via-slate-900 to-slate-900 border border-emerald-500/30 p-5 rounded-2xl shadow-md flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
            <Sparkles className="w-6 h-6 text-emerald-400 animate-pulse" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-white font-bold text-base">WhatsApp AI Bot Training Session</h3>
              {trainingStatus?.needs_retrain ? (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                  Retrain Required (New items/media added)
                </span>
              ) : (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                  AI Knowledge Up to Date
                </span>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              {trainingStatus?.last_trained_at
                ? `Last trained: ${new Date(trainingStatus.last_trained_at).toLocaleString()} • ${trainingStatus.items_count} items • ${trainingStatus.images_count} images • ${trainingStatus.videos_count} videos indexed.`
                : "Not trained yet. Click Train AI to synchronize all products, images, and videos with the WhatsApp bot."}
            </p>
          </div>
        </div>
        <button
          onClick={handleTrainAI}
          disabled={isTrainingAI}
          className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-bold text-sm rounded-xl shadow-lg transition flex items-center gap-2 disabled:opacity-50 shrink-0"
        >
          {isTrainingAI ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {isTrainingAI ? "Training AI Session..." : "Train AI Session"}
        </button>
      </div>

      {trainBannerMsg && (
        <div
          className={`p-4 rounded-xl flex items-center justify-between gap-3 text-sm font-medium ${
            trainBannerMsg.type === "success"
              ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
              : "bg-rose-50 text-rose-800 border border-rose-200"
          }`}
        >
          <div className="flex items-center gap-2">
            {trainBannerMsg.type === "success" ? (
              <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
            )}
            <span>{trainBannerMsg.text}</span>
          </div>
          <button onClick={() => setTrainBannerMsg(null)} className="text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {importMsg && (
        <div
          className={`p-4 rounded-xl flex items-center justify-between gap-3 text-sm font-medium ${
            importMsg.type === "success"
              ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
              : "bg-rose-50 text-rose-800 border border-rose-200"
          }`}
        >
          <div className="flex items-center gap-2">
            {importMsg.type === "success" ? (
              <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
            )}
            <span>{importMsg.text}</span>
          </div>
          <button onClick={() => setImportMsg(null)} className="text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Catalog Tab */}
      {activeTab === "catalog" && (
        <div className="space-y-6">
          {/* Ingestion Box */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Google Sheets Sync */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Link className="w-5 h-5 text-emerald-600" />
                <h2 className="font-semibold text-slate-800">Sync from Google Sheets</h2>
              </div>
              <p className="text-xs text-slate-500 mb-4">
                Paste your Google Sheet link (supports columns: SKU, Name, Price, Stock, Image / Images, Video).
              </p>
              <form onSubmit={handleImportSheet} className="flex gap-2">
                <input
                  type="url"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  value={sheetUrl}
                  onChange={(e) => setSheetUrl(e.target.value)}
                  className="flex-1 px-4 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  required
                />
                <button
                  type="submit"
                  disabled={importingSheet}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-xl transition flex items-center gap-2 shrink-0 disabled:opacity-50"
                >
                  {importingSheet ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Sync Now
                </button>
              </form>
            </div>

            {/* CSV / Excel Drag and Drop */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Upload className="w-5 h-5 text-blue-600" />
                  <h2 className="font-semibold text-slate-800">Upload CSV / Excel File</h2>
                </div>
                <p className="text-xs text-slate-500 mb-4">
                  Bulk import products from CSV or Excel file (.csv, .xlsx).
                </p>
              </div>
              <div>
                <label className="cursor-pointer inline-flex items-center justify-center gap-2 w-full py-2.5 px-4 border-2 border-dashed border-slate-300 hover:border-emerald-500 rounded-xl text-sm font-medium text-slate-700 bg-slate-50 hover:bg-emerald-50/50 transition">
                  <Upload className="w-4 h-4 text-emerald-600" />
                  <span>Choose File to Upload</span>
                  <input
                    type="file"
                    accept=".csv,.xlsx"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={importingSheet}
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Search & Filter Bar */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="relative w-full sm:w-80">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <input
                type="text"
                placeholder="Search products by SKU or title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchItems()}
                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button
                onClick={openAddModal}
                className="w-full sm:w-auto px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition flex items-center justify-center gap-2 shadow-sm"
              >
                <Plus className="w-4 h-4" /> Add Item
              </button>
              <button
                onClick={fetchItems}
                className="w-full sm:w-auto px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-xl transition flex items-center justify-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
              </button>
            </div>
          </div>

          {/* Items Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-slate-600">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-700 font-medium">
                  <tr>
                    <th className="py-3.5 px-4">Item & Visuals</th>
                    <th className="py-3.5 px-4">Media</th>
                    <th className="py-3.5 px-4">SKU</th>
                    <th className="py-3.5 px-4">Category</th>
                    <th className="py-3.5 px-4">Price</th>
                    <th className="py-3.5 px-4">Stock</th>
                    <th className="py-3.5 px-4">Status</th>
                    <th className="py-3.5 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-slate-400">
                        No catalog items found. Click "+ Add Product / Service" above or sync a spreadsheet.
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => {
                      const allImages =
                        item.image_urls && item.image_urls.length > 0
                          ? item.image_urls
                          : item.image_url
                          ? [item.image_url]
                          : [];
                      const hasVideo = Boolean(
                        item.video_url || (item.video_urls && item.video_urls.length > 0)
                      );

                      return (
                        <tr key={item.id} className="hover:bg-slate-50/80 transition">
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-3">
                              <div
                                onClick={() => setPreviewMediaItem(item)}
                                className="relative cursor-pointer group"
                              >
                                {allImages.length > 0 ? (
                                  <img
                                    src={allImages[0]}
                                    alt={item.name}
                                    className="w-12 h-12 object-cover rounded-lg border border-slate-200 group-hover:opacity-80 transition"
                                  />
                                ) : (
                                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 group-hover:bg-slate-200">
                                    <Package className="w-6 h-6" />
                                  </div>
                                )}
                                {allImages.length > 1 && (
                                  <span className="absolute -bottom-1.5 -right-1.5 bg-slate-900 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full border border-white">
                                    +{allImages.length - 1}
                                  </span>
                                )}
                              </div>
                              <div>
                                <p className="font-semibold text-slate-900">{item.name}</p>
                                {item.description && (
                                  <p className="text-xs text-slate-400 line-clamp-1 max-w-xs">
                                    {item.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3.5 px-4">
                            <div className="flex flex-wrap items-center gap-1.5">
                              {allImages.length > 0 && (
                                <span
                                  onClick={() => setPreviewMediaItem(item)}
                                  className="cursor-pointer inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                                >
                                  <ImageIcon className="w-3 h-3" />
                                  {allImages.length} {allImages.length === 1 ? "Img" : "Imgs"}
                                </span>
                              )}
                              {hasVideo && (
                                <span
                                  onClick={() => setPreviewMediaItem(item)}
                                  className="cursor-pointer inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                                >
                                  <Video className="w-3 h-3" /> Video
                                </span>
                              )}
                              {allImages.length === 0 && !hasVideo && (
                                <span className="text-xs text-slate-400 italic">No media</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3.5 px-4 font-mono text-xs text-slate-500">{item.sku}</td>
                          <td className="py-3.5 px-4 text-xs text-slate-500">{item.category || "General"}</td>
                          <td className="py-3.5 px-4">
                            <div className="flex items-baseline gap-1.5">
                              <span className="font-bold text-slate-900">₹{item.discounted_price}</span>
                              {item.price > item.discounted_price && (
                                <span className="text-xs text-slate-400 line-through">₹{item.price}</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3.5 px-4 font-medium">{item.stock_quantity}</td>
                          <td className="py-3.5 px-4">
                            <span
                              className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                                item.stock_status === "in_stock"
                                  ? "bg-emerald-100 text-emerald-800"
                                  : item.stock_status === "low_stock"
                                  ? "bg-amber-100 text-amber-800"
                                  : "bg-rose-100 text-rose-800"
                              }`}
                            >
                              {item.stock_status === "in_stock"
                                ? "In Stock"
                                : item.stock_status === "low_stock"
                                ? "Low Stock"
                                : "Out of Stock"}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => openEditModal(item)}
                                className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
                                title="Edit Product"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteItem(item.id, item.name)}
                                className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                                title="Delete Product"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === "settings" && (
        <form onSubmit={handleSaveProfile} className="space-y-6">
          {profileSavedMsg && (
            <div className="p-4 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl flex items-center gap-3 text-sm font-medium">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <span>{profileSavedMsg}</span>
            </div>
          )}

          {/* Assistant Persona & Voice */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-emerald-600" />
                <h2 className="font-semibold text-slate-800">Master Voice Persona (Multilingual)</h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700">Enable AI Sales Assistant:</span>
                <input
                  type="checkbox"
                  checked={profile.is_enabled}
                  onChange={(e) => setProfile({ ...profile, is_enabled: e.target.checked })}
                  className="w-5 h-5 accent-emerald-600 rounded cursor-pointer"
                />
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Pre-curated studio voices that reply to customer inquiries and voice notes on WhatsApp with human warmth.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <label
                className={`p-4 rounded-xl border-2 cursor-pointer transition flex items-start gap-3 ${
                  profile.voice_persona === "aarohi"
                    ? "border-emerald-500 bg-emerald-50/50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name="voice_persona"
                  value="aarohi"
                  checked={profile.voice_persona === "aarohi"}
                  onChange={() => setProfile({ ...profile, voice_persona: "aarohi" })}
                  className="mt-1 accent-emerald-600"
                />
                <div>
                  <p className="font-bold text-slate-900 flex items-center gap-2">
                    👩 Aarohi — Warm & Charming Female
                    <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-semibold">Recommended</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Friendly, professional, relationship-manager cadence. Fluent in Hindi, Marathi, and English.
                  </p>
                </div>
              </label>

              <label
                className={`p-4 rounded-xl border-2 cursor-pointer transition flex items-start gap-3 ${
                  profile.voice_persona === "kabir"
                    ? "border-emerald-500 bg-emerald-50/50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name="voice_persona"
                  value="kabir"
                  checked={profile.voice_persona === "kabir"}
                  onChange={() => setProfile({ ...profile, voice_persona: "kabir" })}
                  className="mt-1 accent-emerald-600"
                />
                <div>
                  <p className="font-bold text-slate-900">👨 Kabir — Confident & Authoritative Male</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Deep, reassuring, executive sales consultant tone. Perfect for high-ticket and professional services.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Business & Pricing Rules */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              <h2 className="font-semibold text-slate-800">Deterministic Commercial Rules</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Business Name</label>
                <input
                  type="text"
                  value={profile.business_name}
                  onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Business Category</label>
                <input
                  type="text"
                  value={profile.business_category || ""}
                  onChange={(e) => setProfile({ ...profile, business_category: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  placeholder="e.g. Cosmetics, Electronics, Services"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Free Shipping Threshold (₹)</label>
                <input
                  type="number"
                  value={profile.free_shipping_threshold}
                  onChange={(e) => setProfile({ ...profile, free_shipping_threshold: Number(e.target.value) })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Standard Shipping Fee (₹)</label>
                <input
                  type="number"
                  value={profile.standard_shipping_fee}
                  onChange={(e) => setProfile({ ...profile, standard_shipping_fee: Number(e.target.value) })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>

              <div className="sm:col-span-2 flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="tax_inclusive"
                  checked={profile.tax_inclusive}
                  onChange={(e) => setProfile({ ...profile, tax_inclusive: e.target.checked })}
                  className="w-4 h-4 accent-emerald-600 rounded cursor-pointer"
                />
                <label htmlFor="tax_inclusive" className="text-sm font-medium text-slate-700 cursor-pointer">
                  Catalog prices are inclusive of GST / Taxes
                </label>
              </div>
            </div>
          </div>

          {/* Autonomous AI Sales & Negotiation Engine */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              <h2 className="font-semibold text-slate-800">Autonomous AI Sales & Negotiation Engine</h2>
            </div>
            <p className="text-xs text-slate-500">
              Configures ultra-human consultative selling, objection handling (fabric quality, zero see-through, sizing fit, COD reassurance), and multi-lingual dialect mirroring on WhatsApp.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Sales Persona & Tone</label>
                <select
                  value={profile.sales_tone || "consultative_boutique"}
                  onChange={(e) => setProfile({ ...profile, sales_tone: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white"
                >
                  <option value="consultative_boutique">🌸 Consultative Boutique Stylist (Empathetic, complimentary, fabric & sizing expert)</option>
                  <option value="luxury_reassuring">👑 Luxury Reassuring (High-trust, prestige vocabulary, VIP white-glove)</option>
                  <option value="friendly_direct">⚡ Friendly & Direct (Quick value, concise, fast closing)</option>
                </select>
                <p className="text-[11px] text-slate-400 mt-1">Adapts how the bot compliments choices, addresses doubts, and closes orders.</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Language & Dialect Mode</label>
                <select
                  value={profile.language_mode || "adaptive_hinglish"}
                  onChange={(e) => setProfile({ ...profile, language_mode: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white"
                >
                  <option value="adaptive_hinglish">🇮🇳 Adaptive Hinglish & Roman Urdu (Natural conversational dialect mirroring)</option>
                  <option value="formal_english">🇬🇧 English Only (Polished international styling)</option>
                  <option value="hindi_only">🇮🇳 Hindi Script</option>
                </select>
                <p className="text-[11px] text-slate-400 mt-1">Automatically mirrors customer's language style (e.g. Hinglish, Roman Urdu, English).</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">AI Intelligence Provider</label>
                <select
                  value={profile.ai_provider || "auto"}
                  onChange={(e) => setProfile({ ...profile, ai_provider: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white"
                >
                  <option value="auto">⚡ Auto (Optimized Cloud LLM with Instant Neural Local Fallback)</option>
                  <option value="gemini">Google Gemini (Gemini 1.5 Flash)</option>
                  <option value="openai">OpenAI (GPT-4o-mini / GPT-4o)</option>
                  <option value="groq">Groq (Llama 3.3 70B Fast)</option>
                  <option value="openrouter">OpenRouter (Multi-model router)</option>
                  <option value="local">🔒 Local Neural Sales Synthesizer (Zero-latency, 100% offline)</option>
                </select>
                <p className="text-[11px] text-slate-400 mt-1">Cloud LLM generates dynamic dialogue while preserving strict catalog pricing boundaries.</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Model Name (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. gemini-1.5-flash, gpt-4o-mini, llama-3.3-70b-versatile"
                  value={profile.ai_model_name || ""}
                  onChange={(e) => setProfile({ ...profile, ai_model_name: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
                <p className="text-[11px] text-slate-400 mt-1">Leave empty to use provider's recommended default model.</p>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1">Custom AI API Key (Optional)</label>
                <input
                  type="password"
                  placeholder="sk-... or gsk_... (leave empty to use system-wide API keys)"
                  value={profile.ai_api_key || ""}
                  onChange={(e) => setProfile({ ...profile, ai_api_key: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none font-mono"
                />
                <p className="text-[11px] text-slate-400 mt-1">Optional tenant override. If omitted, the server's configured environment keys will be used.</p>
              </div>
            </div>
          </div>

          {/* Operational & Hot Lead Alerts */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center gap-2">
              <PhoneCall className="w-5 h-5 text-emerald-600" />
              <h2 className="font-semibold text-slate-800">Hot Lead Alerts & Quiet Hours</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Sales Manager Alert Phone</label>
                <input
                  type="text"
                  placeholder="+919876543210"
                  value={profile.sales_rep_phone || ""}
                  onChange={(e) => setProfile({ ...profile, sales_rep_phone: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
                <p className="text-[11px] text-slate-400 mt-1">Receives WhatsApp alert when lead score ≥ 75.</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Quiet Hours Start (24h)</label>
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={profile.quiet_hours_start}
                  onChange={(e) => setProfile({ ...profile, quiet_hours_start: Number(e.target.value) })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
                <p className="text-[11px] text-slate-400 mt-1">e.g. 21 for 9:00 PM</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Quiet Hours End (24h)</label>
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={profile.quiet_hours_end}
                  onChange={(e) => setProfile({ ...profile, quiet_hours_end: Number(e.target.value) })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
                <p className="text-[11px] text-slate-400 mt-1">e.g. 9 for 9:00 AM</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={savingProfile}
              className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl shadow-md transition disabled:opacity-50 flex items-center gap-2"
            >
              {savingProfile && <RefreshCw className="w-4 h-4 animate-spin" />}
              Save All AI Settings
            </button>
          </div>
        </form>
      )}

      {/* Question Module Tab */}
      {activeTab === "questions" && (
        <div className="space-y-6">
          {questionBannerMsg && (
            <div
              className={`p-4 rounded-xl flex items-center justify-between gap-3 text-sm font-medium ${
                questionBannerMsg.type === "success"
                  ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                  : "bg-rose-50 text-rose-800 border border-rose-200"
              }`}
            >
              <div className="flex items-center gap-2">
                {questionBannerMsg.type === "success" ? (
                  <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
                )}
                <span>{questionBannerMsg.text}</span>
              </div>
              <button
                onClick={() => setQuestionBannerMsg(null)}
                className="text-slate-400 hover:text-slate-600 text-xs font-bold"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Module Overview & Global Toggle */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
              <div>
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100">
                    <ListOrdered className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="font-bold text-slate-900 text-lg">Lead Qualification Question Sequence</h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Sequential qualification flow triggered when a customer message does not match catalog items or menu numbers.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2.5">
                <button
                  type="button"
                  onClick={handleSeedDefaults}
                  className="flex items-center gap-2 px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold transition"
                  title="Auto-populate standard qualification questions based on your business category"
                >
                  <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                  Seed Standard Questions
                </button>
                <button
                  type="button"
                  onClick={openAddQuestionModal}
                  className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-semibold shadow-sm transition"
                >
                  <Plus className="w-4 h-4" />
                  Add Question
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={profile.intake_questions_enabled !== false}
                  onChange={async (e) => {
                    const updated = { ...profile, intake_questions_enabled: e.target.checked };
                    setProfile(updated);
                    try {
                      const url = new URL("/api/v1/catalog/ai-profile", window.location.origin);
                      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
                      await fetch(url.toString(), {
                        method: "PUT",
                        headers: {
                          Authorization: `Bearer ${token}`,
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify(updated),
                      });
                      setQuestionBannerMsg({
                        type: "success",
                        text: `Question Module is now ${e.target.checked ? "ENABLED" : "DISABLED"}.`,
                      });
                    } catch (err) {
                      console.error("Failed to update intake status", err);
                    }
                  }}
                  className="w-5 h-5 accent-emerald-600 rounded cursor-pointer"
                />
                <div>
                  <span className="text-sm font-bold text-slate-800 block">
                    Enable Sequential Qualification Flow
                  </span>
                  <span className="text-xs text-slate-500 block">
                    When active, unmatched inquiries ask these questions step-by-step and recommend the best matching catalog product.
                  </span>
                </div>
              </label>

              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-200 text-slate-700">
                  {questions.filter((q) => q.is_active).length} Active Questions
                </span>
              </div>
            </div>
          </div>

          {/* Intro & Completion Messages */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-emerald-600" />
                <h3 className="font-semibold text-slate-800 text-sm">Sequence Introductory & Completion Messages</h3>
              </div>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const url = new URL("/api/v1/catalog/ai-profile", window.location.origin);
                    if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
                    await fetch(url.toString(), {
                      method: "PUT",
                      headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify(profile),
                    });
                    setQuestionBannerMsg({ type: "success", text: "Sequence messages saved successfully!" });
                  } catch (err) {
                    console.error("Failed to save sequence messages", err);
                  }
                }}
                className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-semibold transition"
              >
                Save Messages
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Introductory Greeting Message (Before Q1)
                </label>
                <textarea
                  rows={2}
                  value={profile.intake_intro_message || ""}
                  onChange={(e) => setProfile({ ...profile, intake_intro_message: e.target.value })}
                  placeholder={`Welcome to *${profile.business_name}*! 🌸 Let's find exactly what you're looking for.`}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Sent immediately preceding Question 1 to set a warm, welcoming context.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Completion Message (Before Product Recommendation)
                </label>
                <textarea
                  rows={2}
                  value={profile.intake_completion_message || ""}
                  onChange={(e) => setProfile({ ...profile, intake_completion_message: e.target.value })}
                  placeholder="Thank you! Based on your preferences, here is our top recommendation for you:"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Sent after the last question is answered, right before the matched product & photo recommendation.
                </p>
              </div>
            </div>
          </div>

          {/* Sequential Questions Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-800 text-sm">Sequence Questions</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">
                  {questions.length} total
                </span>
              </div>
              <p className="text-xs text-slate-500">
                Use the <ArrowUp className="w-3 h-3 inline" /> and <ArrowDown className="w-3 h-3 inline" /> arrows to reorder questions in the sequence.
              </p>
            </div>

            {loadingQuestions ? (
              <div className="p-12 text-center text-slate-400">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-emerald-600" />
                <p className="text-sm">Loading qualification questions...</p>
              </div>
            ) : questions.length === 0 ? (
              <div className="p-12 text-center space-y-4">
                <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto text-slate-400">
                  <ListOrdered className="w-7 h-7" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-800 text-base">No Qualification Questions Configured</h4>
                  <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                    Click "Seed Standard Questions" to populate default questions for your category (e.g. Style, Size, Fabric, Location), or click "+ Add Question" to build your own sequence.
                  </p>
                </div>
                <div className="flex items-center justify-center gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleSeedDefaults}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-semibold shadow-sm transition"
                  >
                    <Sparkles className="w-4 h-4 text-amber-300" />
                    Seed Standard Questions Now
                  </button>
                  <button
                    type="button"
                    onClick={openAddQuestionModal}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold transition"
                  >
                    <Plus className="w-4 h-4" />
                    Add Custom Question
                  </button>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-600">
                  <thead className="bg-slate-50 text-[11px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="py-3 px-4 w-20 text-center">Order</th>
                      <th className="py-3 px-4">Question Text (WhatsApp)</th>
                      <th className="py-3 px-4 w-44">Lead Field Key</th>
                      <th className="py-3 px-4">Quick Replies / Options</th>
                      <th className="py-3 px-4 w-28 text-center">Status</th>
                      <th className="py-3 px-4 w-28 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {questions.map((q, idx) => (
                      <tr key={q.id} className="hover:bg-slate-50/70 transition">
                        <td className="py-3.5 px-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <span className="w-6 h-6 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold flex items-center justify-center">
                              #{idx + 1}
                            </span>
                            <div className="flex flex-col ml-1">
                              <button
                                type="button"
                                disabled={idx === 0}
                                onClick={() => handleMoveQuestion(idx, "up")}
                                className="text-slate-400 hover:text-slate-700 disabled:opacity-20 p-0.5"
                                title="Move up"
                              >
                                <ArrowUp className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                disabled={idx === questions.length - 1}
                                onClick={() => handleMoveQuestion(idx, "down")}
                                className="text-slate-400 hover:text-slate-700 disabled:opacity-20 p-0.5"
                                title="Move down"
                              >
                                <ArrowDown className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </td>

                        <td className="py-3.5 px-4">
                          <p className="font-semibold text-slate-800 text-sm">{q.question_text}</p>
                          {q.help_text && (
                            <p className="text-xs text-slate-400 mt-0.5">{q.help_text}</p>
                          )}
                        </td>

                        <td className="py-3.5 px-4">
                          <span className="font-mono text-xs px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-md text-slate-700">
                            {q.field_key}
                          </span>
                        </td>

                        <td className="py-3.5 px-4">
                          {q.quick_replies && q.quick_replies.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {q.quick_replies.map((qr, qrIdx) => (
                                <span
                                  key={qrIdx}
                                  className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200"
                                >
                                  {qrIdx + 1}️⃣ {qr}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 italic">Free text input</span>
                          )}
                        </td>

                        <td className="py-3.5 px-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleToggleQuestionActive(q)}
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold transition ${
                              q.is_active
                                ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                                : "bg-slate-200 text-slate-600 hover:bg-slate-300"
                            }`}
                          >
                            {q.is_active ? "Active" : "Disabled"}
                          </button>
                        </td>

                        <td className="py-3.5 px-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => openEditQuestionModal(q)}
                              className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
                              title="Edit question"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteQuestion(q.id, q.question_text)}
                              className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                              title="Delete question"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Interactive Flow Visualizer Card */}
          <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 text-white p-6 rounded-2xl shadow-xl border border-slate-800">
            <div className="flex items-center gap-2.5 mb-3">
              <Bot className="w-5 h-5 text-emerald-400" />
              <h3 className="font-bold text-base">How the Lead Qualification Sequence Works Live on WhatsApp</h3>
            </div>
            <p className="text-xs text-slate-300 mb-6">
              Simulated demonstration of customer interaction when an unmatched requirement is received:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700/80 space-y-2">
                <div className="flex items-center gap-2 text-emerald-400 font-bold">
                  <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-[10px]">1</span>
                  Unmatched Inquiry Trigger
                </div>
                <div className="bg-slate-950/60 p-2.5 rounded-lg text-slate-300 font-mono text-[11px]">
                  👤 <em>Customer:</em> "Mujhe function ke liye party dress chahiye"
                </div>
                <p className="text-slate-400 text-[11px]">
                  Bot detects inquiry is not a specific catalog SKU. Bot starts Question 1 with numbered quick replies!
                </p>
              </div>

              <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700/80 space-y-2">
                <div className="flex items-center gap-2 text-emerald-400 font-bold">
                  <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-[10px]">2</span>
                  Step-by-Step Questions
                </div>
                <div className="bg-slate-950/60 p-2.5 rounded-lg text-slate-300 font-mono text-[11px]">
                  🤖 <em>Bot:</em> "Question 1 of 4: What style are you looking for?"
                  <br />
                  👤 <em>Customer:</em> "2" (Butterfly Farasha)
                </div>
                <p className="text-slate-400 text-[11px]">
                  Bot saves choice to customer memory facts, advances to Question 2, 3, and 4 seamlessly.
                </p>
              </div>

              <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700/80 space-y-2">
                <div className="flex items-center gap-2 text-emerald-400 font-bold">
                  <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-[10px]">3</span>
                  Recommendation & Rep Alert
                </div>
                <div className="bg-slate-950/60 p-2.5 rounded-lg text-slate-300 font-mono text-[11px]">
                  🤖 <em>Bot:</em> "Here is our top recommendation for you: ⭐ Farasha Butterfly Burqa (₹2,499) + [Photo]"
                </div>
                <p className="text-slate-400 text-[11px]">
                  Bot summarizes preferences, recommends matching item with photo, and alerts sales rep on WhatsApp!
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Intake Question Modal */}
      {isQuestionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-xl w-full my-8 max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2.5">
                <ListOrdered className="w-5 h-5 text-emerald-600" />
                <h3 className="font-bold text-lg text-slate-900">
                  {editingQuestion ? "Edit Qualification Question" : "Add Qualification Question"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsQuestionModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-200/50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveQuestion} className="p-6 overflow-y-auto space-y-4 flex-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Sequence Order <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={questionFormData.sequence_order}
                    onChange={(e) =>
                      setQuestionFormData({ ...questionFormData, sequence_order: Number(e.target.value) })
                    }
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    required
                  />
                  <p className="text-[11px] text-slate-400 mt-1">Order position in the question sequence.</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Lead Profile Field Key <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. style_preference"
                    value={questionFormData.field_key}
                    onChange={(e) =>
                      setQuestionFormData({ ...questionFormData, field_key: e.target.value })
                    }
                    className="w-full px-3 py-2 text-sm font-mono border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    required
                  />
                  <p className="text-[11px] text-slate-400 mt-1">Identifies this answer in lead facts.</p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Question Text (Sent on WhatsApp) <span className="text-rose-500">*</span>
                </label>
                <textarea
                  rows={3}
                  placeholder="e.g. What style or item are you looking for? (Daily Wear Burqa, Butterfly Farasha, Dubai Luxury Abaya...)"
                  value={questionFormData.question_text}
                  onChange={(e) =>
                    setQuestionFormData({ ...questionFormData, question_text: e.target.value })
                  }
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Help Text / Description (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Customer's desired outfit style or collection"
                  value={questionFormData.help_text}
                  onChange={(e) =>
                    setQuestionFormData({ ...questionFormData, help_text: e.target.value })
                  }
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>

              {/* Quick Replies Manager */}
              <div className="space-y-2">
                <label className="block text-xs font-semibold text-slate-700">
                  Quick Reply Options (Optional)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="e.g. Butterfly Farasha"
                    value={questionFormData.quick_reply_input}
                    onChange={(e) =>
                      setQuestionFormData({ ...questionFormData, quick_reply_input: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addQuickReply();
                      }
                    }}
                    className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={addQuickReply}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-semibold transition"
                  >
                    Add Option
                  </button>
                </div>

                {questionFormData.quick_replies.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {questionFormData.quick_replies.map((qr, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-200"
                      >
                        <span>{idx + 1}️⃣ {qr}</span>
                        <button
                          type="button"
                          onClick={() => removeQuickReply(idx)}
                          className="text-emerald-600 hover:text-rose-600 p-0.5 rounded-full"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-slate-400">
                  Options are presented as numbered choices. Customers can reply with numbers (1, 2) or text.
                </p>
              </div>

              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={questionFormData.is_active}
                    onChange={(e) =>
                      setQuestionFormData({ ...questionFormData, is_active: e.target.checked })
                    }
                    className="w-4 h-4 accent-emerald-600 rounded cursor-pointer"
                  />
                  Question Active
                </label>

                <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={questionFormData.is_required}
                    onChange={(e) =>
                      setQuestionFormData({ ...questionFormData, is_required: e.target.checked })
                    }
                    className="w-4 h-4 accent-emerald-600 rounded cursor-pointer"
                  />
                  Required Step
                </label>
              </div>

              <div className="pt-4 border-t border-slate-200 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsQuestionModalOpen(false)}
                  className="px-4 py-2 border border-slate-300 hover:bg-slate-100 text-slate-700 rounded-xl text-xs font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingQuestion}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-semibold shadow-md transition disabled:opacity-50 flex items-center gap-2"
                >
                  {savingQuestion && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  {editingQuestion ? "Update Question" : "Save Question"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-3xl w-full my-8 max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2.5">
                <Package className="w-6 h-6 text-emerald-600" />
                <h3 className="font-bold text-lg text-slate-900">
                  {editingItem ? "Edit Product / Service" : "Add New Product / Service"}
                </h3>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-200/50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveItem} className="p-6 overflow-y-auto space-y-6 flex-1">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Basic Details
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Product or Service Title <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. E-Commerce Website Development"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      SKU (Optional, Auto-generated if left blank)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. WEB-ECOM-01"
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      className="w-full px-3.5 py-2 text-sm font-mono border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Category
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Website, SEO, Mobile App, Service"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Pricing & Stock */}
              <div className="space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Pricing & Inventory
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      MRP / Regular Price (₹)
                    </label>
                    <input
                      type="number"
                      min={0}
                      required
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Selling / Discounted Price (₹)
                    </label>
                    <input
                      type="number"
                      min={0}
                      required
                      value={formData.discounted_price}
                      onChange={(e) =>
                        setFormData({ ...formData, discounted_price: Number(e.target.value) })
                      }
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Stock Quantity
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={formData.stock_quantity}
                      onChange={(e) =>
                        setFormData({ ...formData, stock_quantity: Number(e.target.value) })
                      }
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Stock Status
                    </label>
                    <select
                      value={formData.stock_status}
                      onChange={(e) => setFormData({ ...formData, stock_status: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    >
                      <option value="in_stock">In Stock</option>
                      <option value="low_stock">Low Stock</option>
                      <option value="out_of_stock">Out of Stock</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="block text-xs font-semibold text-slate-700">
                  Description / Deliverables / Key Features
                </label>
                <textarea
                  rows={3}
                  placeholder="Explain what is included in this product or service for the AI bot to explain to customers..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>

              {/* Multiple Images Upload & Gallery */}
              <div className="space-y-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                      Product Images Gallery ({formData.image_urls.length})
                    </span>
                  </div>
                  {uploadingMedia && (
                    <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                      <RefreshCw className="w-3 h-3 animate-spin" /> Uploading media...
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Add multiple photos. When a customer on WhatsApp asks "sample dikhao" or "photos bhejo", the AI bot will automatically dispatch these photos!
                </p>

                {/* Upload or URL inputs */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <label className="cursor-pointer px-4 py-2 bg-white border border-slate-300 hover:border-emerald-500 rounded-xl text-xs font-medium text-slate-700 hover:bg-emerald-50/40 transition flex items-center justify-center gap-1.5 shrink-0">
                    <Upload className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Upload Image Files</span>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleUploadImages}
                      disabled={uploadingMedia}
                      className="hidden"
                    />
                  </label>

                  <div className="flex-1 flex gap-2">
                    <input
                      type="url"
                      placeholder="Or paste image URL (https://...)"
                      value={imageUrlInput}
                      onChange={(e) => setImageUrlInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addImageUrl())}
                      className="flex-1 px-3 py-1.5 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white"
                    />
                    <button
                      type="button"
                      onClick={addImageUrl}
                      className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-medium"
                    >
                      Add URL
                    </button>
                  </div>
                </div>

                {/* Thumbnail Previews */}
                {formData.image_urls.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 pt-2">
                    {formData.image_urls.map((img, idx) => (
                      <div
                        key={idx}
                        className="relative group rounded-xl overflow-hidden border-2 border-slate-200 bg-white aspect-square shadow-xs"
                      >
                        <img src={img} alt={`Thumb ${idx}`} className="w-full h-full object-cover" />
                        {idx === 0 && (
                          <span className="absolute top-1 left-1 bg-emerald-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md">
                            Cover
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute top-1 right-1 bg-rose-600 text-white p-1 rounded-md opacity-0 group-hover:opacity-100 transition shadow-sm"
                          title="Remove image"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Video Showcase */}
              <div className="space-y-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Video className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                      Product Demonstration Video
                    </span>
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Upload a video demo (MP4, MOV, WebM up to 50MB) or paste a direct video link. When customers ask "video bhejo" or "demo video", the bot will send this video!
                </p>

                {formData.video_url ? (
                  <div className="space-y-2">
                    <div className="relative rounded-xl overflow-hidden border border-slate-300 bg-black max-h-48 flex items-center justify-center">
                      <video
                        src={formData.video_url}
                        controls
                        className="max-h-48 w-full object-contain"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500 font-mono truncate max-w-md">
                        {formData.video_url}
                      </span>
                      <button
                        type="button"
                        onClick={removeVideo}
                        className="px-2.5 py-1 text-xs bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-lg flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" /> Remove Video
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <label className="cursor-pointer px-4 py-2 bg-white border border-slate-300 hover:border-blue-500 rounded-xl text-xs font-medium text-slate-700 hover:bg-blue-50/40 transition flex items-center justify-center gap-1.5 shrink-0">
                      <Upload className="w-3.5 h-3.5 text-blue-600" />
                      <span>Upload Video File (Max 50MB)</span>
                      <input
                        type="file"
                        accept="video/*"
                        onChange={handleUploadVideo}
                        disabled={uploadingMedia}
                        className="hidden"
                      />
                    </label>

                    <div className="flex-1 flex gap-2">
                      <input
                        type="url"
                        placeholder="Or paste video URL (https://...)"
                        value={videoUrlInput}
                        onChange={(e) => setVideoUrlInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addVideoUrl())}
                        className="flex-1 px-3 py-1.5 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                      />
                      <button
                        type="button"
                        onClick={addVideoUrl}
                        className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-medium"
                      >
                        Set Video
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="p-4 bg-slate-50 -mx-6 -mb-6 border-t border-slate-200 flex justify-end gap-3 rounded-b-2xl">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingItem || uploadingMedia}
                  className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow-md transition disabled:opacity-50 flex items-center gap-2"
                >
                  {savingItem && <RefreshCw className="w-4 h-4 animate-spin" />}
                  {editingItem ? "Update Product" : "Save Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Gallery / Media Inspection Preview Modal */}
      {previewMediaItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full p-6 text-white space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div>
                <h3 className="font-bold text-lg text-white">{previewMediaItem.name}</h3>
                <p className="text-xs text-slate-400">Media gallery and demo preview</p>
              </div>
              <button
                onClick={() => setPreviewMediaItem(null)}
                className="text-slate-400 hover:text-white p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              {/* Images */}
              {previewMediaItem.image_urls && previewMediaItem.image_urls.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2">
                    Images ({previewMediaItem.image_urls.length})
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {previewMediaItem.image_urls.map((u, i) => (
                      <div key={i} className="aspect-square rounded-xl overflow-hidden bg-slate-800 border border-slate-700">
                        <img
                          src={u}
                          alt={`${previewMediaItem.name} ${i + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).src = "/catalog_media/niqab_black_1.jpg";
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Video */}
              {(previewMediaItem.video_url || (previewMediaItem.video_urls && previewMediaItem.video_urls.length > 0)) && (
                <div>
                  <h4 className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-2">
                    Video Demonstration
                  </h4>
                  <video
                    src={previewMediaItem.video_url || previewMediaItem.video_urls?.[0]}
                    controls
                    preload="metadata"
                    playsInline
                    className="w-full max-h-64 rounded-xl bg-black border border-slate-700"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end pt-2 border-t border-slate-800">
              <button
                onClick={() => setPreviewMediaItem(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-xl text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
